import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import LandingPage from "./pages/landing/LandingPage";
import Registration from "./pages/registration/Registration";
import { clearStoredSession, getCurrentSession } from "./api/loginApi";
import LanguageRuntime from "./i18n/LanguageRuntime";
import GlobalTooltip from "./components/tooltip/GlobalTooltip";
import { getStoredLanguage, persistLanguage } from "./i18n/i18n";

const loadOwnerDashboard = () => import("./pages/dashboard/owner/OwnerDashboard");
const loadManagerDashboard = () => import("./pages/dashboard/manager/ManagerDashboard");
const loadEmployeeDashboard = () => import("./pages/dashboard/employee/EmployeeDashboard");
const loadSuperAdminDashboard = () => import("./pages/dashboard/superadmin/SuperAdminDashboard");
const loadInviteAccept = () => import("./pages/invite/InviteAccept");
const loadLogin = () => import("./pages/login/Login");
const loadForgotPassword = () => import("./pages/login/ForgotPassword");
const loadRegistration = () => import("./pages/registration/Registration");

const OwnerDashboard = lazy(loadOwnerDashboard);
const ManagerDashboard = lazy(loadManagerDashboard);
const EmployeeDashboard = lazy(loadEmployeeDashboard);
const SuperAdminDashboard = lazy(loadSuperAdminDashboard);
const InviteAccept = lazy(loadInviteAccept);
const Login = lazy(loadLogin);
const ForgotPassword = lazy(loadForgotPassword);
function normalizeRoute(route) {
  return route === "register" ? "registration" : route;
}

function normalizeUserRole(role) {
  const normalizedRole = String(role || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (normalizedRole === "superadmin") return "super_admin";
  if (normalizedRole === "member") return "employee";
  return normalizedRole;
}

function getRouteFromHash() {
  const hashRoute = window.location.hash.replace(/^#\/?/, "").replace(/^\/+|\/+$/g, "");
  if (hashRoute) return normalizeRoute(hashRoute);
  const path = window.location.pathname.replace(/^\/+|\/+$/g, "");
  const superAdminMatch = path.match(/(?:^|\/)superadmin\/(dashboard|companies|company\/\d+|plans|logs|bugs|revenue)$/i);
  if (superAdminMatch) return `superadmin/${superAdminMatch[1]}`;
  const pathRoute = path.split("/").filter(Boolean).pop() || "home";
  return normalizeRoute(["home", "login", "forgot-password", "register", "registration", "dashboard"].includes(pathRoute) ? pathRoute : "home");
}

function isSuperAdminRoute(route) {
  return /^superadmin\/(dashboard|companies|company\/\d+|plans|logs|bugs|revenue)$/.test(String(route || "").toLowerCase());
}

function getAuthenticatedRoute(role, requestedRoute = getRouteFromHash()) {
  const normalizedRole = normalizeUserRole(role);
  const normalizedRoute = String(requestedRoute || "").toLowerCase();
  if (normalizedRole === "super_admin") {
    return isSuperAdminRoute(normalizedRoute) ? normalizedRoute : "superadmin/dashboard";
  }
  return "dashboard";
}

function replaceHashRoute(route, clearPublicRoute = false) {
  const cleanRoute = route === "home" ? "" : route;
  const appPath = clearPublicRoute
    ? window.location.pathname.replace(/\/(home|login|register|registration)\/?$/i, "/")
    : window.location.pathname;
  const search = clearPublicRoute ? "" : window.location.search;
  window.history.replaceState(null, "", `${appPath}${search}#/${cleanRoute}`);
}

function App() {
  const inviteToken = new URLSearchParams(window.location.search).get("invite");
  const [screen, setScreen] = useState(inviteToken ? "invite" : getRouteFromHash());
  const [authState, setAuthState] = useState("checking");
  const [sessionData, setSessionData] = useState(null);
  const [language, setLanguage] = useState(getStoredLanguage);
  const [authenticatedRoute, setAuthenticatedRoute] = useState(getRouteFromHash());
  const authRequestVersion = useRef(0);

  const changeLanguage = useCallback((nextLanguage) => {
    setLanguage((current) => {
      const requested = nextLanguage || (current === "hi" ? "en" : "hi");
      const normalized = persistLanguage(requested);
      if (normalized !== current) {
        window.dispatchEvent(new CustomEvent("kaamdesk:language-changed", { detail: normalized }));
      }
      return normalized;
    });
  }, []);
  const toggleLanguage = useCallback(() => changeLanguage(), [changeLanguage]);

  useEffect(() => {
    const handleLanguageRequest = (event) => changeLanguage(event.detail);
    window.addEventListener("kaamdesk:set-language", handleLanguageRequest);
    return () => window.removeEventListener("kaamdesk:set-language", handleLanguageRequest);
  }, [changeLanguage]);

  useEffect(() => {
    const pickerSelector = 'input[type="date"], input[type="datetime-local"], input[type="month"], input[type="week"]';
    const openPickerFromField = (event) => {
      const input = event.target?.closest?.(pickerSelector);
      if (!input || input.disabled || input.readOnly || input.closest(".kd-employee-leave-reference-date-field") || typeof input.showPicker !== "function") return;
      try {
        input.showPicker();
      } catch (_) {
        // Keep the browser's native click behavior as the fallback.
      }
    };

    document.addEventListener("click", openPickerFromField, true);
    return () => document.removeEventListener("click", openPickerFromField, true);
  }, []);

  useEffect(() => {
    let active = true;
    const requestVersion = authRequestVersion.current;
    loadOwnerDashboard(); loadManagerDashboard(); loadEmployeeDashboard(); loadSuperAdminDashboard();
    const restoreSession = async () => {
      // A persistent cookie can be written by a just-finished login/registration
      // response while React is mounting. A single bounded retry avoids a false
      // redirect without keeping public page loads slow.
      let response = await getCurrentSession();
      if (!response?.data) {
        await new Promise((resolve) => window.setTimeout(resolve, 160));
        response = await getCurrentSession();
      }
      return response;
    };
    restoreSession().then((response) => {
      if (!active || requestVersion !== authRequestVersion.current) return;
      if (response?.data) {
        const nextRoute = getAuthenticatedRoute(response.data.role, getRouteFromHash());
        setSessionData(response.data); setAuthState("authenticated");
        setAuthenticatedRoute(nextRoute); setScreen("dashboard"); replaceHashRoute(nextRoute, true);
      } else {
        setSessionData(null); setAuthState("guest");
        if (getRouteFromHash() === "dashboard") { setScreen("login"); window.location.hash = "#/login"; }
      }
    }).catch(() => {
      if (!active || requestVersion !== authRequestVersion.current) return;
      clearStoredSession(); setSessionData(null); setAuthState("guest");
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const handleSessionExpired = () => {
      clearStoredSession();
      setSessionData(null);
      setAuthState("guest");
      setScreen("login");
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}#/login`);
    };

    window.addEventListener("kaamdesk:session-expired", handleSessionExpired);
    return () => window.removeEventListener("kaamdesk:session-expired", handleSessionExpired);
  }, []);

  useEffect(() => {
    const preloadPublicRoutes = () => { loadLogin(); loadForgotPassword(); loadRegistration(); if (inviteToken) loadInviteAccept(); };
    const idleId = window.requestIdleCallback
      ? window.requestIdleCallback(preloadPublicRoutes, { timeout: 1200 })
      : window.setTimeout(preloadPublicRoutes, 250);
    return () => window.cancelIdleCallback ? window.cancelIdleCallback(idleId) : window.clearTimeout(idleId);
  }, [inviteToken]);

  useEffect(() => {
    const syncRoute = () => {
      if (authState === "authenticated") {
        const requested = getRouteFromHash();
        const nextRoute = getAuthenticatedRoute(sessionData?.role, requested);
        setAuthenticatedRoute(nextRoute); setScreen("dashboard");
        if (requested !== nextRoute) replaceHashRoute(nextRoute, true);
        return;
      }
      if (!inviteToken) {
        const requested = getRouteFromHash();
        if (requested === "dashboard") {
          window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}#/login`);
          setScreen("login");
        } else setScreen(requested);
      }
    };
    window.addEventListener("hashchange", syncRoute); window.addEventListener("popstate", syncRoute);
    return () => { window.removeEventListener("hashchange", syncRoute); window.removeEventListener("popstate", syncRoute); };
  }, [authState, inviteToken, sessionData]);

  const navigate = (nextScreen) => {
    if (authState === "authenticated" && nextScreen !== "dashboard") nextScreen = "dashboard";
    window.location.hash = nextScreen === "home" ? "#/" : `#/${nextScreen}`;
    setScreen(nextScreen); window.scrollTo(0, 0);
  };

  const completeAuthentication = async (verifiedUser = null) => {
    // Ignore an older page-load session request after login succeeds.
    authRequestVersion.current += 1;

    // The password-login response is only the verification step. Confirm the
    // actual PHP session before entering the protected dashboard so the UI can
    // never show a dashboard that the server cannot authenticate.
    // The registration endpoint rotates the PHP session id. A just-written
    // Set-Cookie can take one event loop turn to become available to an XHR;
    // retrying twice avoids treating that short hand-off as a logged-out user.
    let response = null;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      response = await getCurrentSession();
      if (response?.data) break;
      if (attempt < 2) await new Promise((resolve) => window.setTimeout(resolve, 180));
    }
    const nextSession = response?.data || null;
    if (!nextSession) throw new Error("Your login session could not be restored. Please try again.");

    const nextRoute = getAuthenticatedRoute(nextSession?.role || verifiedUser?.role);
    setSessionData(nextSession); setAuthenticatedRoute(nextRoute); setAuthState("authenticated");
    replaceHashRoute(nextRoute, true); setScreen("dashboard");
  };

  const loadingFallback = <main className="dashboard-loading" aria-live="polite">Loading...</main>;
  const transitionFallback = null;
  const guardedScreen = authState === "authenticated" ? "dashboard" : screen;
  const withLanguageRuntime = (content) => <><LanguageRuntime key={`language-runtime-${language}`} language={language} /><GlobalTooltip language={language} />{content}</>;
  if (authState === "checking" && !["login", "forgot-password", "registration", "home"].includes(screen)) return withLanguageRuntime(loadingFallback);

  if (guardedScreen === "invite") return withLanguageRuntime(<Suspense fallback={transitionFallback}><InviteAccept token={inviteToken} onRegistrationComplete={completeAuthentication} /></Suspense>);
  if (guardedScreen === "registration") return withLanguageRuntime(<Registration language={language} onToggleLanguage={toggleLanguage} onBackHome={() => navigate("home")} onGoLogin={() => navigate("login")} onRegistrationComplete={completeAuthentication} />);
  if (guardedScreen === "login") return withLanguageRuntime(<Suspense fallback={transitionFallback}><Login language={language} onToggleLanguage={toggleLanguage} onBackHome={() => navigate("home")} onRegister={() => navigate("registration")} onForgotPassword={() => navigate("forgot-password")} onLoginSuccess={completeAuthentication} /></Suspense>);
  if (guardedScreen === "forgot-password") return withLanguageRuntime(<Suspense fallback={transitionFallback}><ForgotPassword language={language} onToggleLanguage={toggleLanguage} onBackHome={() => navigate("home")} onGoLogin={() => navigate("login")} /></Suspense>);
  if (guardedScreen === "dashboard") {
    const role = normalizeUserRole(sessionData?.role) || "employee";
    const DashboardByRole = role === "super_admin" ? SuperAdminDashboard : role === "owner" ? OwnerDashboard : role === "manager" ? ManagerDashboard : EmployeeDashboard;
    const changeAuthenticatedRoute = (nextRoute) => {
      const safeRoute = role === "super_admin" && isSuperAdminRoute(nextRoute) ? nextRoute : "dashboard";
      setAuthenticatedRoute(safeRoute); replaceHashRoute(safeRoute, true); window.scrollTo(0, 0);
    };
    const dashboardProps = {
      initialSession: sessionData,
      language,
      onToggleLanguage: toggleLanguage,
      route: authenticatedRoute,
      onRouteChange: changeAuthenticatedRoute,
      onLogout: () => {
        clearStoredSession(); setSessionData(null);
        window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}#/`);
        setAuthState("guest"); setScreen("home");
      },
    };
    if (["manager", "employee"].includes(role)) {
      dashboardProps.onAutoLogout = () => {
        clearStoredSession(); setSessionData(null);
        window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}#/login`);
        setAuthState("guest"); setScreen("login");
      };
    }
    return withLanguageRuntime(<Suspense fallback={transitionFallback}><DashboardByRole {...dashboardProps} /></Suspense>);
  }
  return withLanguageRuntime(<LandingPage language={language} onToggleLanguage={toggleLanguage} onStart={() => navigate("registration")} onLogin={() => navigate("login")} />);
}

export default App;
